import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notificationInput } from '@trafiku/contracts';
import { Archive, Send } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ConfirmDialog, Dialog } from '@/components/ui/dialog';
import { Field, Input, Textarea } from '@/components/ui/form';
import { Badge, Card, PageHeader, Pagination } from '@/components/ui/layout';
import { DataTable } from '@/components/ui/table';
import { api } from '@/lib/api';
import { useFormState } from '@/lib/form';
import { formatDateTime } from '@/lib/format';
import { t } from '@/lib/i18n';
import { useAction, useList } from '@/lib/resources';
import type { Notification } from '@/lib/types';

function ArchiveButton({ id }: { id: string }) {
  const archive = useAction(`/notifications/${id}/archive`, ['notifications'], t('Archived'));
  return (
    <Button
      size="sm"
      variant="ghost"
      icon={<Archive className="size-4" />}
      loading={archive.isPending}
      onClick={() => archive.mutate(undefined)}
    >
      {t('Archive')}
    </Button>
  );
}

export function NotificationsPage() {
  const [page, setPage] = useState(1);
  const list = useList<Notification>('notifications', { page, pageSize: 25, is_active: true });
  const [composing, setComposing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const form = useFormState({ title: '', body: '' });
  const qc = useQueryClient();
  const send = useMutation({
    mutationFn: (input: { title: string; body: string }) =>
      api<{ sent: boolean }>('/notifications', { method: 'POST', body: input }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
      if (r.sent) toast.success(t('Sent to all app users'));
      else toast.warning(t('Saved, but sending failed. Check the Firebase configuration.'));
      setComposing(false);
      setConfirming(false);
      form.setValues({ title: '', body: '' });
    },
  });

  return (
    <>
      <PageHeader
        title={t('Push notifications')}
        subtitle={t('Messages sent to every app user.')}
        actions={
          <Button icon={<Send className="size-4" />} onClick={() => setComposing(true)}>
            {t('New notification')}
          </Button>
        }
      />
      <Card padded={false}>
        <DataTable
          rows={list.data?.items}
          loading={list.isFetching}
          empty={t('No notifications sent yet')}
          columns={[
            {
              header: t('Message'),
              cell: (n) => (
                <div>
                  <div className="font-medium">{n.title}</div>
                  <div className="text-xs text-slate-500">{n.body}</div>
                </div>
              ),
            },
            { header: t('Sent'), cell: (n) => formatDateTime(n.created_at) },
            { header: t('By'), cell: (n) => n.created_by?.email ?? '—' },
            { header: '', cell: (n) => <ArchiveButton id={n._id} />, className: 'text-right' },
          ]}
        />
        {list.data && list.data.total > 0 && (
          <Pagination page={page} pageSize={25} total={list.data.total} onPage={setPage} />
        )}
      </Card>
      <Dialog
        open={composing}
        onClose={() => setComposing(false)}
        title={t('New push notification')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setComposing(false)}>
              {t('Cancel')}
            </Button>
            <Button
              icon={<Send className="size-4" />}
              onClick={() => {
                const parsed = notificationInput.safeParse(form.values);
                if (parsed.success) setConfirming(true);
                else form.submit(notificationInput, async () => {});
              }}
            >
              {t('Send…')}
            </Button>
          </>
        }
      >
        <Field label={t('Title')} error={form.errors.title}>
          {(fid) => (
            <Input
              id={fid}
              maxLength={100}
              value={form.values.title}
              onChange={(e) => form.set('title', e.target.value)}
            />
          )}
        </Field>
        <Field label={t('Message')} error={form.errors.body} hint={`${form.values.body.length}/500`}>
          {(fid) => (
            <Textarea
              id={fid}
              maxLength={500}
              value={form.values.body}
              onChange={(e) => form.set('body', e.target.value)}
            />
          )}
        </Field>
        <Badge tone="amber">{t('Goes to every phone with the app installed')}</Badge>
      </Dialog>
      <ConfirmDialog
        open={confirming}
        onCancel={() => setConfirming(false)}
        onConfirm={() => send.mutate(form.values)}
        loading={send.isPending}
        danger={false}
        confirmLabel={t('Send now')}
        title={t('Send to all users?')}
        message={
          <>
            <strong>{form.values.title}</strong>
            <br />
            {form.values.body}
          </>
        }
      />
    </>
  );
}
